import React, { useState } from 'react';
import Select, { OptionProps } from 'react-select';
import { useTranslation } from 'react-i18next';
import { useListQuery } from 'http-client/hooks/useCrud';
import { API_ENDPOINTS } from 'http-client/httpConstants';
import { customSearchStyles } from 'pages/Medicine/Search/searchStyles';

interface Inventory {
  shopId: string;
  shopItemId: string;
  shopItemName: string;
  location: string;
  shelfLevel: number;
  quantity: number;
  sellPrice: number;
  isActive: boolean;
  deletedAt: boolean;
  updatedAt: string;
  nearestExpiryDate?: string | null;
}

interface Props {
  isUrdu?: boolean;
  onMedicineSelect: (inventory: Inventory) => void;
  className?: string;
  placeholder?: string;
  width?: number | string;
  shopId: string;
}

/**
 * Custom option for Inventory list
 */
const CustomOption = (props: OptionProps<any, false>) => {
  const { data, innerRef, innerProps, isFocused } = props;
  const inv: Inventory = data.data;

  const outOfStock = !inv.quantity || inv.quantity <= 0;

  return (
    <div
      ref={innerRef}
      {...innerProps}
      className={`d-flex justify-content-between align-items-center px-3 py-2 ${
        isFocused ? 'bg-primary bg-opacity-25' : ''
      }`}
      style={{ cursor: 'pointer' }}
    >
      {/* Name */}
      <div style={{ flex: 2, fontWeight: 600 }}>{inv.shopItemName}</div>

      {/* Location */}
      <div style={{ flex: 1 }}>{inv.location}</div>

      {/* Shelf */}
      <div style={{ flex: 1 }}>Shelf {inv.shelfLevel}</div>

      {/* Price */}
      <div style={{ flex: 1 }}>{inv.sellPrice} PKR</div>

      {/* Quantity */}
      <div
        style={{
          flex: 1,
          color: outOfStock ? 'red' : 'inherit',
          fontWeight: outOfStock ? 700 : 400,
        }}
      >
        {outOfStock ? 'Out of stock' : inv.quantity}
      </div>

      {/* Expiry */}
      <div style={{ flex: 1 }}>{inv.nearestExpiryDate ?? '—'}</div>
    </div>
  );
};

const SelectInventorySearch: React.FC<Props> = ({
  isUrdu = false,
  onMedicineSelect,
  className = '',
  placeholder,
  width = '100%',
  shopId,
}) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');

  /** Load inventory list */
  const { data: inventoryData } = useListQuery<Inventory>(
    API_ENDPOINTS.TENANT.INVENTORIES.SHOP_BASE,
    { shopId },
  );

  /** Apply search filter */
  const filteredOptions = (inventoryData || [])
    .filter((item) => item.shopItemName.toLowerCase().includes(query.toLowerCase()))
    .map((inv) => ({
      label: inv.shopItemName,
      value: inv.shopItemId,
      data: inv,
      isDisabled: inv.quantity === 0 || inv.quantity == null,
    }));

  const handleChange = (selected: any) => {
    if (selected && onMedicineSelect) {
      onMedicineSelect(selected.data);
      setQuery('');
    }
  };

  const noOptionsMessage = () => t('No results found');

  return (
    <div className={className} style={{ width }}>
      <Select
        options={filteredOptions}
        onInputChange={(value) => setQuery(value)}
        onChange={handleChange}
        value={null}
        placeholder={placeholder || t('Search items...')}
        styles={customSearchStyles(isUrdu)}
        noOptionsMessage={noOptionsMessage}
        isClearable
        isSearchable
        inputValue={query}
        menuPlacement="auto"
        openMenuOnClick={false}
        menuIsOpen={query.length > 0}
        components={{ Option: CustomOption }}
      />
    </div>
  );
};

export default SelectInventorySearch;
